/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

/**
 * @typedef {Object} OpenIdIntegration
 * @property {boolean} OpenIdIntegration.isOrganizationNameVisible
 * @property {string} OpenIdIntegration.id
 * @property {string} OpenIdIntegration.name
 * @property {boolean} OpenIdIntegration.enabled
 * @property {string} OpenIdIntegration.description
 * @property {string} OpenIdIntegration.type
 * @property {string} OpenIdIntegration.category
 * @property {number} OpenIdIntegration.createdAt
 * @property {number} OpenIdIntegration.updatedAt
 * @property {string} OpenIdIntegration.issuer
 * @property {string} OpenIdIntegration.authorizationEndpoint
 * @property {string} OpenIdIntegration.tokenEndpoint
 * @property {string} OpenIdIntegration.jwksUri
 * @property {string} OpenIdIntegration.grantType
 * @property {string} OpenIdIntegration.clientId
 * @property {string} OpenIdIntegration.challengeMethod
 * @property {string} OpenIdIntegration.additionalScopes
 * @property {string} OpenIdIntegration.emailKey
 * @property {string} OpenIdIntegration.fullNameKey
 */

/**
 * @typedef {Object} OrganizationLoginSettings
 * @property {boolean} OrganizationLoginSettings.isOrganizationNameVisible
 * @property {boolean} OrganizationLoginSettings.isOrganizationLogoVisible
 * @property {boolean} OrganizationLoginSettings.isCustomMessageVisible
 * @property {boolean} OrganizationLoginSettings.customMessage
 * @property {boolean} OrganizationLoginSettings.isCredentialLoginVisible
 * @property {boolean} OrganizationLoginSettings.isGoogleLoginVisible
 * @property {boolean} OrganizationLoginSettings.isMicrosoftLoginVisible
 * @property {boolean} OrganizationLoginSettings.isSkipNativeLoginEnabled - When this is true and there is only one integration, the user will be redirected to the integration login page directly.
 * @property {boolean} OrganizationLoginSettings.isAutoUserCreationEnabled
 * @property {boolean} OrganizationLoginSettings.isPostAuthenticationEnabled
 * @property {OpenIdIntegration[]} OrganizationLoginSettings.integrations
 * @property {string} OrganizationLoginSettings.defaultAutoCreationRole
 * @property {string} OrganizationLoginSettings.postAuthenticationRequestUrl
 * @property {object} OrganizationLoginSettings.roleMap
 * @property {string} OrganizationLoginSettings.supportedIntegrationCategories
 */

/**
 * @typedef {Object} IntegrationLoginBody
 * @property {string} IntegrationLoginBody.code
 * @property {string} IntegrationLoginBody.codeVerifier
 * @property {string} IntegrationLoginBody.redirectUri
 * @property {string} IntegrationLoginBody.integrationId
 * @property {string} IntegrationLoginBody.organizationId
 */

/**
 * @typedef {Object} OidcInfoForStartingAuthorizationOptions
 * @property {string} OidcInfoForStartingAuthorizationOptions.redirectUri - The redirect URI to use in the OIDC request.
 * @property {Object} OidcInfoForStartingAuthorizationOptions.extras
 * @property {String} OidcInfoForStartingAuthorizationOptions.extras.response_mode - The response mode to use in the OIDC request (usually it's "query" or "fragment").
 */

/**
  * Add two numbers together, then pass the results to a callback function.
  *
  * @callback IntegrationCallback
  * @param {IntegrationLoginBody} body - The request body to send to the backend in order to log user
  */
